<template>
  <!-- Change status popup -->
  <div class="popup-overlay">
    <div
      class="popup attendee-status-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="attendee-status-title"
    >
      <!-- Popup header -->
      <div class="popup__header d-flex justify-content-between">
        <div class="popup__header-title">{{ displayName }}</div>
        <button
          class="popup__close"
          @click="close"
          type="button"
          aria-label="Close"
        >
          <img
            src="../assets/images/attendees/icon-close.svg"
            alt=""
            aria-hidden="true"
          />
        </button>
      </div>

      <!-- Popup body -->
      <div class="popup__body">
        <div class="popup__title" id="attendee-status-title">
          Change status to
        </div>
        <p v-if="settingsError" class="check-in-error" role="alert">{{ settingsError }}</p>

        <!-- Status INVITED -->
        <div class="status-list" v-if="!IsProgram">
          <button
            type="button"
            class="status-list__item"
            @click="selectedStatus = 'invited'"
            :class="{ active: selectedStatus === 'invited' }"
            :aria-pressed="selectedStatus === 'invited'"
          >
            <img
              src="../assets/images/attendees/icon-status-invited.svg"
              alt=""
              aria-hidden="true"
            />
            Invited
          </button>
        </div>

        <!-- Status REGISTERED -->
        <div class="status-list">
          <button
            type="button"
            class="status-list__item"
            @click="selectedStatus = 'registered'"
            :class="{ active: selectedStatus === 'registered' }"
            :aria-pressed="selectedStatus === 'registered'"
          >
            <img
              src="../assets/images/attendees/icon-status-registered.svg"
              alt=""
              aria-hidden="true"
            />
            Registered
          </button>
        </div>

        <!-- Status DECLINED -->
        <div class="status-list" v-if="!IsProgram">
          <button
            type="button"
            class="status-list__item"
            @click="selectedStatus = 'declined'"
            :class="{ active: selectedStatus === 'declined' }"
            :aria-pressed="selectedStatus === 'declined'"
          >
            <img
              src="../assets/images/attendees/icon-status-declined.svg"
              alt=""
              aria-hidden="true"
            />
            Declined
          </button>
        </div>

        <!-- Status CHECKED-IN -->
        <div class="status-list">
          <button
            type="button"
            class="status-list__item"
            @click="selectedStatus = 'present'"
            :class="{ active: selectedStatus === 'present' }"
            :aria-pressed="selectedStatus === 'present'"
          >
            <img
              src="../assets/images/attendees/icon-status-checked-in.svg"
              alt=""
              aria-hidden="true"
            />
            Checked-in
          </button>
        </div>

        <div v-if="selectedStatus === 'present'" class="check-in-media">
          <p v-if="checkingSignature">Checking signature status...</p>
          <div v-if="signatureNeeded">
            <p>Attendee signature required for check-in.</p>
            <SignaturePad ref="signaturePad" @change="signatureReady = $event" />
          </div>
          <PhotoCapture v-if="settings?.photoEnabled && !photoDisabled" @change="photo = $event" />
        </div>
        <p v-if="error" class="check-in-error" role="alert">{{ error }}</p>
      </div>

      <!-- Popup buttons/footer -->
      <div class="popup__footer">
        <button @click="close" class="btn btn--default btn--sm" type="button">Cancel</button>
        <button
          @click="confirm"
          class="btn btn--primary btn--sm ms-2"
          type="button"
          :disabled="selectedStatus == null || sending || checkingSignature || (signatureNeeded && !signatureReady)"
        >
          {{ sending ? "Saving..." : "Confirm" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import SignaturePad from "./SignaturePadComponent.vue";
import PhotoCapture from "./PhotoCaptureComponent.vue";

export default {
  name: "AttendeeStatusComponent",
  components: { SignaturePad, PhotoCapture },
  props: {
    Name: String,
    EventParticipantId: String,
    EventProgramParticipantId: String,
    EventId: String,
    ProgramId: String,
    IsProgram: Boolean,
  },
  data: () => ({ selectedStatus: null, hasSignature: false, signatureNeeded: false, signatureReady: false, checkingSignature: false, sending: false, photo: null, photoDisabled: false, error: "" }),
  emits: ["closeAttendeeStatus"],
  computed: {
    displayName() {
      return String(this.Name || "").trim() || "Attendee";
    },
    eventId() { return this.EventId || this.$store.getters.getEvent?.EventID || this.$store.getters.getProgram?.eventId; },
    settings() { return this.$store.getters.getCheckInSettings; },
    settingsError() { return this.$store.getters.getCheckInSettingsError; },
  },
  watch: {
    async selectedStatus(status) {
      this.error = "";
      if (status !== "present") { this.signatureNeeded = false; return; }
      await this.loadSignatureState();
    },
  },
  methods: {
    close() {
      this.$emit("closeAttendeeStatus");
    },
    async loadSignatureState() {
      this.checkingSignature = true;
      try {
        await this.$store.dispatch("loadCheckInSettings", this.eventId);
        const url = process.env.VUE_APP_ROOT_API + "/v1/DataAPI/GetCheckinParticipant/json?" + new URLSearchParams({ EventID: this.eventId, EventParticipantID: this.EventParticipantId });
        const response = await fetch(url);
        if (!response.ok) throw new Error("Unable to check signature status.");
        const result = await response.json();
        this.hasSignature = !!result.data?.files?.hasSignature;
      } catch (_) {
        this.hasSignature = false;
        this.error = "Unable to verify the saved signature. The server will check it when you confirm.";
      } finally {
        this.signatureNeeded = !!this.settings?.signatureRequired && !this.hasSignature;
        this.checkingSignature = false;
      }
    },
    async confirm() {
      if (this.selectedStatus == null || this.sending) return;
      this.sending = true;
      this.error = "";
      try {
        const signature = this.signatureNeeded ? this.$refs.signaturePad?.image() : null;
        if (this.signatureNeeded && !signature) { this.error = "Please sign before checking in."; return; }
        const statusId = this.$store.getters.getParticipantStatusId(this.selectedStatus);
        if (!statusId) throw new Error("Participant statuses are unavailable. Please sign in again.");
        const url = process.env.VUE_APP_ROOT_API + "/v1/DataAPI/VisitorSetStatus/json?" + new URLSearchParams({ EventID: this.eventId, EventParticipantID: this.EventParticipantId, ...(this.IsProgram ? { ProgramID: this.ProgramId } : {}), StatusID: statusId });
        const response = await fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ signature, photo: this.selectedStatus === "present" ? this.photo : null }) });
        if (!response.ok) throw new Error("Unable to update status. Please try again.");
        const result = await response.json();
        if (result.data?.ErrorCode === "SIGNATURE_REQUIRED") { this.signatureNeeded = true; this.error = result.data.Msg; return; }
        if (result.data?.ErrorCode === "PHOTO_NOT_ENABLED") { this.photo = null; this.photoDisabled = true; this.error = result.data.Msg; return; }
        if (result.serviceStatus !== "OK") { this.error = result.data?.Msg || "Unable to update status."; return; }
        this.photo = null;
        this.$emit("closeAttendeeStatus");
      } catch (error) {
        this.error = error.message || "Unable to update status.";
      } finally { this.sending = false; }
    },
  },
};
</script>
